import { Chat, ChatMessage } from "@/components/chat/chat"
import { Button } from "@/components/ui/button"
import { Page } from "@/components/page"
import { usePage } from "@/components/page/hooks"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { createAI, examplePlaybook, migrate, PLAYBOOK_FORMAT, playbooks, traceHook, validate } from "@/lib/agents/playbooks"
import { supabase } from "@/lib/data"
import { PlaybookItems } from "./components/items"
import { Activity, LiveCall, track } from "./components/activity"
import { DebugTurn, StateButton, TurnDebug } from "./components/debug"
import { Designer } from "./components/designer"
import { fakeAI } from "./components/fakeai"
import { BugIcon, Workflow } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"

export const playbooks_crud = {
  title: "Playbook",
  route: "/dev/playbooks/:id",
  icon: Workflow,
  component,
  group: "dev",
  resource: "agents",
  action: "read",
} satisfies PageConfig

// "local" works on example.ts and never reads the db. "db" loads the row of the url
const SOURCE = "db" as "local" | "db"
// With SOURCE "local", Save replaces the row of the url with the example. Off unless that is the intent
const SAVE_EXAMPLE = false
// Saves keep the versions they replace, newest first, up to this many
const HISTORY = 25

type SimulatorMessage = ChatMessage & { turn?: DebugTurn, state?: PlaybookState }

function toHistory(messages: ChatMessage[]): PlaybookMessage[] {
  return messages.filter(m => m.text).map(m => ({ role: m.role === "user" ? "user" : "assistant", message: m.text! }))
}

function blankPlaybook(): Playbook {
  return { id: crypto.randomUUID(), format: PLAYBOOK_FORMAT, flows: { main: { items: [] } } }
}

function component() {
  const { team, id, navigate } = usePage()
  const [playbook, setPlaybook] = useState<Playbook | null>(SOURCE === "local" ? examplePlaybook : null)
  const [state, setState] = useState<PlaybookState | null>(null)
  const [messages, setMessages] = useState<SimulatorMessage[]>([])
  const [debug, setDebug] = useState(true)
  const [focus, setFocus] = useState<PlaybookCursor>()
  const [live, setLive] = useState<LiveCall[] | null>(null)
  const [realAI, setRealAI] = useState(false)
  const booted = useRef(false)
  const counter = useRef(0)
  const ai = useMemo(() => team?.id ? createAI(team.id) : null, [team?.id])
  const busy = live !== null

  useEffect(() => {
    if (SOURCE === "db") load()
  }, [id])

  useEffect(() => {
    if (!playbook || booted.current) return
    booted.current = true
    send(null, {}, [], playbook)
  }, [playbook])

  async function load() {
    if (!id) return setPlaybook(blankPlaybook())
    const { data, error } = await supabase.from("playbooks").select("*").eq("id", id).single()
    if (error) return toast.error(`The playbook could not be loaded: ${error.message}`)
    if (!data.data) return setPlaybook({ ...blankPlaybook(), title: data.title ?? undefined })
    let migration: ReturnType<typeof migrate>
    try { migration = migrate(data.data) } catch (e) { return toast.error((e as Error).message) }
    setPlaybook(migration.playbook)
    if (!migration.changed) return
    const what = `Migrated from format ${migration.from} to ${PLAYBOOK_FORMAT}: ${migration.notes.join("; ")}`
    const errors = validate(migration.playbook).filter(i => i.level === "error").length
    if (errors) toast.warning(`${what}. It has ${errors} errors: fix them and save`)
    else if (!migration.silent) toast.warning(`${what}. Review it and save`)
    else if (await save(migration.playbook)) toast.success(`${what}. Saved`)
  }

  async function save(next: Playbook) {
    const errors = validate(next).filter(i => i.level === "error").length
    if (errors) { toast.error(`${errors} errors block the save`); return false }
    const row = { title: next.title, data: { ...next, format: PLAYBOOK_FORMAT } as any }
    if (!id) {
      const { data, error } = await supabase.from("playbooks").insert({ ...row, team: team?.id }).select().single()
      if (error) { toast.error(`The playbook could not be saved: ${error.message}`); return false }
      navigate(`/dev/playbooks/${data.id}`)
      return true
    }
    const { data: current, error: readError } = await supabase.from("playbooks").select("title, data, history").eq("id", id).single()
    if (readError) { toast.error(`The playbook could not be saved: ${readError.message}`); return false }
    const history = (current.history ?? []) as unknown as PlaybookHistoryEntry[]
    const replaced: PlaybookHistoryEntry[] = current.data ? [{ title: current.title ?? undefined, replaced_at: new Date().toISOString(), data: current.data as any }] : []
    const { error } = await supabase.from("playbooks").update({ ...row, history: [...replaced, ...history].slice(0, HISTORY) as any }).eq("id", id)
    if (error) { toast.error(`The playbook could not be saved: ${error.message}`); return false }
    return true
  }

  async function handleSave() {
    if (playbook && await save(playbook)) toast.success("Playbook saved")
  }

  function nextId(prefix: string) {
    return `${prefix}${counter.current++}`
  }

  async function send(previous: PlaybookState | null, input: PlaybookInput, history: PlaybookMessage[] = [], current = playbook!) {
    setLive([])
    const before = structuredClone(previous?.vars ?? {})
    const global = { lastMessage: input.message, messageCount: history.filter(m => m.role === "user").length + (input.message ? 1 : 0) }
    const activity: PlaybookHook = (event, run) => { if (event === "tool.before" || event === "tool.after") setLive(prev => track(prev, run.step!.call!, event === "tool.after")) }
    const turn = await playbooks.play<PlaybookTracedRun>({ playbook: current, state: previous, input, history, global, hooks: [traceHook, activity], ai: realAI && ai ? ai : fakeAI })
    setState(turn.state)
    const text = [turn.output.response, ...(turn.output.files ?? []).map(url => `📎 ${url}`)].filter(Boolean).join("\n\n")
    const image = turn.output.images?.[0]
    const debugTurn = { trace: turn.trace ?? [], before, after: structuredClone(turn.state.vars) }
    setMessages(prev => [...prev, { id: nextId("a"), role: "agent", text, image, quickReplies: turn.output.quick_replies, turn: debugTurn, state: turn.state }])
    setLive(null)
  }

  function handleSend(text: string) {
    setMessages(prev => [...prev, { id: nextId("u"), role: "user", text }])
    send(state, { id: nextId("m"), message: text }, toHistory(messages))
  }

  function handleQuickReply(id: string) {
    setMessages(prev => [...prev, { id: nextId("u"), role: "user", text: id }])
    send(state, { id: nextId("m"), message: id }, toHistory(messages))
  }

  function footer(turn: DebugTurn) {
    return <>
      <Activity calls={turn.trace.flatMap(t => t.event === "tool.after" && t.call ? [{ call: t.call, done: true }] : [])} />
      {debug && <TurnDebug turn={turn} flows={playbook.flows} onFocus={setFocus} />}
    </>
  }

  const shown: ChatMessage[] = messages.map(m => m.turn ? { ...m, footer: footer(m.turn) } : m)
  if (live) shown.push({ id: "live", role: "agent", footer: <Activity calls={live} thinking /> })

  function handleReset() {
    setMessages([])
    send(null, {})
  }

  function handleRewind(id: string) {
    const index = messages.findIndex(m => m.id === id)
    if (index < 0 || !messages[index].state) return
    setState(messages[index].state!)
    setMessages(messages.slice(0, index + 1))
  }

  if (!playbook) return <Page />

  return <Page>
    <PlaybookItems playbook={playbook} cursor={state?.cursor} focus={focus} onChange={setPlaybook} onSave={SOURCE === "db" || SAVE_EXAMPLE ? handleSave : undefined} aside={
      <Tabs defaultValue="agent" className="h-full">
        <div className="flex items-center">
          <TabsList>
            <TabsTrigger value="agent">Agent</TabsTrigger>
            <TabsTrigger value="design">Design</TabsTrigger>
          </TabsList>
          <div className="grow" />
          <Label className="text-xs gap-2"><Switch checked={realAI} onCheckedChange={setRealAI} disabled={!ai} />Real AI</Label>
        </div>
        <TabsContent value="agent" className="min-h-0 h-full -mr-2">
          <Chat messages={shown}
            busy={busy} onSend={handleSend} onQuickReply={handleQuickReply} onReset={handleReset} onRewind={handleRewind} title="Playbook simulator"
            actions={<>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant={debug ? "secondary" : "outline"} size="icon" aria-label="Debug" onClick={() => setDebug(!debug)}><BugIcon /></Button>
                </TooltipTrigger>
                <TooltipContent><p>{debug ? "Hide debug" : "Show debug"}</p></TooltipContent>
              </Tooltip>
              <StateButton state={state} />
            </>}
          />
        </TabsContent>
        <TabsContent value="design" keepMounted className="min-h-0 h-full -mr-2">
          <Designer playbook={playbook} team={team?.id} realAI={realAI && !!ai} onChange={setPlaybook} />
        </TabsContent>
      </Tabs>
    } />
  </Page>
}
